/**
 * The frame every page sits in: an icon rail, a top bar, the command palette, and the content.
 *
 * Keyboard: ⌘K / Ctrl-K opens the palette from anywhere, g-then-letter jumps between pages
 * (g i inbox, g h handoffs, g p pipeline, g c contacts, g k catalogue, g ? health), and every
 * interactive thing in here is a real button or link so Tab order is the DOM order.
 *
 * Author: Danish Khan
 */
import clsx from 'clsx';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { API_SOURCE } from '../lib/api.ts';
import { useStreamStatus } from '../lib/events.tsx';
import { formatAgo } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useTheme } from '../lib/themeProvider.tsx';
import { canManagePeople } from '../lib/pii.ts';
import { onInstallAvailability, promptInstall } from '../lib/pwa.ts';
import { CommandPalette, type CommandGroup } from './CommandPalette.tsx';
import { Button } from './Button.tsx';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  key: string;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Home', icon: '◉', key: 'g' },
  { to: '/inbox', label: 'Inbox', icon: '✉', key: 'i' },
  { to: '/handoffs', label: 'Handoffs', icon: '⇄', key: 'h' },
  { to: '/pipeline', label: 'Pipeline', icon: '▦', key: 'p' },
  { to: '/contacts', label: 'Contacts', icon: '☰', key: 'c' },
  { to: '/catalogue', label: 'Catalogue', icon: '◫', key: 'k' },
  // '/system', not '/health': the SERVER owns GET /health (app.ts) and returns JSON there for
  // release.sh, Caddy and every uptime check. In-app navigation to /health worked because the
  // router intercepted the click, so the page looked fine — but a refresh, a bookmark or a pasted
  // link fell through to the API and put {"ok":true,"service":"h1-core"} on screen. A page you can
  // only reach by not reloading is a page that breaks the first time somebody shares it.
  { to: '/system', label: 'Health', icon: '♡', key: '?' },
  { to: '/people', label: 'People', icon: '⚇', key: 'u' },
  // Phase 2. The routes and the keyboard map are settled first so the shell, the palette and the
  // g-chords do not move under the user as each screen lands; every one of these renders a page
  // that says it is not built yet rather than an empty frame that reads as "no data".
  { to: '/campaigns', label: 'Campaigns', icon: '➤', key: 'm' },
  { to: '/studio', label: 'Config Studio', icon: '⚙', key: 's' },
  { to: '/quality', label: 'Quality', icon: '✓', key: 'q' },
  { to: '/analytics', label: 'Analytics', icon: '◴', key: 'a' },
  { to: '/onboarding', label: 'Onboarding', icon: '✧', key: 'o' },
];

export function Shell({ children }: { children: ReactNode }): ReactNode {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [pendingG, setPendingG] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { session, signOut } = useSession();
  const { mode, toggle } = useTheme();

  const { data: inbox } = useQuery({
    queryKey: ['inbox', { view: 'all' }],
    queryFn: () => api.inbox({ view: 'all' }),
    staleTime: 30_000,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable === true;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      /**
       * The second key of the chord is checked FIRST, and that order is the whole fix.
       *
       * Home's key is `g` (the palette says so on its row: "Home · g g"), and with the `e.key ===
       * 'g'` branch above this one, the second `g` was swallowed by the "start a chord" branch and
       * simply restarted the chord. Every other page was reachable and Home — the one the palette
       * advertises first — never was. Found by an e2e journey pressing the shortcuts the palette
       * claims exist.
       */
      if (pendingG) {
        const match = NAV.find((n) => n.key === e.key);
        setPendingG(false);
        if (match) {
          e.preventDefault();
          navigate(match.to);
          return;
        }
      }
      if (e.key === 'g') {
        setPendingG(true);
        window.setTimeout(() => setPendingG(false), 1200);
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, pendingG]);

  const groups = useMemo<CommandGroup[]>(() => {
    const pages: CommandGroup = {
      heading: 'Go to',
      items: NAV.map((n) => ({
        id: `nav-${n.to}`,
        label: n.label,
        shortcut: `g ${n.key}`,
        keywords: [n.to.replace('/', '')],
        run: () => navigate(n.to),
      })),
    };
    const leads: CommandGroup = {
      heading: 'Recent leads',
      items: (inbox?.rows ?? []).slice(0, 8).map((c) => ({
        id: `lead-${c.leadId}`,
        label: c.displayName,
        hint: `${c.tier} · ${c.stage}`,
        keywords: [c.stage, c.tier, c.lang, c.lastMessagePreview.slice(0, 40)],
        run: () => navigate(`/inbox/${c.conversationId}`),
      })),
    };
    const actions: CommandGroup = {
      heading: 'Actions',
      items: [
        {
          id: 'act-theme',
          label: mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
          keywords: ['dark', 'light', 'theme', 'contrast'],
          run: toggle,
        },
        {
          id: 'act-unanswered',
          label: 'Show unanswered conversations',
          keywords: ['inbox', 'sla', 'waiting'],
          run: () => navigate('/inbox?view=unanswered'),
        },
        {
          id: 'act-open-handoffs',
          label: 'Show open handoffs',
          keywords: ['claim', 'queue'],
          run: () => navigate('/handoffs?status=open'),
        },
        { id: 'act-signout', label: 'Sign out', keywords: ['logout', 'exit'], run: () => void signOut() },
      ],
    };
    return [pages, leads, actions];
  }, [inbox, navigate, mode, toggle, signOut]);

  const user = session?.user;
  const title = NAV.find((n) => (n.to === '/' ? location.pathname === '/' : location.pathname.startsWith(n.to)))?.label;

  return (
    <div className="flex h-full min-h-0 bg-bg">
      <nav
        aria-label="Sections"
        className="flex w-14 shrink-0 flex-col items-center gap-1 border-r border-line bg-surface py-3"
      >
        <span
          aria-hidden
          className="mb-2 flex size-8 items-center justify-center rounded-[var(--h1-radius)] bg-brand text-sm font-bold text-brand-fg"
          title="H1 Console"
        >
          H1
        </span>
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            title={`${item.label} (g ${item.key})`}
            className={({ isActive }) =>
              clsx(
                'flex size-9 items-center justify-center rounded-[var(--h1-radius)] text-lg transition-colors',
                isActive ? 'bg-brand-soft text-brand-soft-fg' : 'text-fg-subtle hover:bg-surface-2 hover:text-fg',
              )
            }
          >
            <span aria-hidden>{item.icon}</span>
            <span className="sr-only">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
          <h1 className="text-sm font-semibold text-fg">{title ?? 'H1 Console'}</h1>
          {API_SOURCE === 'mock' ? (
            <span
              className="rounded-full bg-warn-soft px-2 py-0.5 text-[11px] font-semibold text-warn-soft-fg"
              title="Every page on this build reads src/mock/. The switch to the real API is in src/lib/api.ts."
            >
              Mock data
            </span>
          ) : (
            <StreamBadge />
          )}

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="ml-auto flex h-8 items-center gap-2 rounded-[var(--h1-radius)] border border-line px-2.5 text-xs text-fg-muted hover:bg-surface-2 hover:text-fg"
          >
            Search
            <kbd className="rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd>
          </button>

          <InstallButton />

          <Button variant="ghost" size="sm" onClick={toggle} aria-label={`Switch to ${mode === 'dark' ? 'light' : 'dark'} theme`}>
            <span aria-hidden>{mode === 'dark' ? '☀' : '☾'}</span>
          </Button>

          {user && (
            <div className="flex items-center gap-2 border-l border-line pl-3">
              <div className="text-right leading-tight">
                <p className="text-xs font-medium text-fg">{user.name}</p>
                <p className="text-[10px] text-fg-subtle">
                  {user.role}
                  {!canManagePeople(user.role) && ' · limited'}
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={() => void signOut()}>
                Sign out
              </Button>
            </div>
          )}
        </header>

        <main className="min-h-0 flex-1 overflow-hidden">{children}</main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} groups={groups} />
    </div>
  );
}

/**
 * Install this console as an app — shown only when the browser has offered to.
 *
 * `beforeinstallprompt` fires when the browser's own installability criteria are met and the
 * person has not installed or dismissed it already, and it cannot be triggered from here. A button
 * rendered unconditionally would do nothing in Safari, nothing in an already-installed window and
 * nothing on a second visit after a dismissal — three quiet lies for one convenience. So there is
 * no button at all until there is something behind it.
 */
function InstallButton(): ReactNode {
  const [available, setAvailable] = useState(false);
  useEffect(() => onInstallAvailability(setAvailable), []);
  if (!available) return null;
  return (
    <Button
      variant="secondary"
      size="sm"
      data-testid="install-app"
      onClick={() => void promptInstall()}
      title="Install the console as an app: its own window, its own icon, and the same live data — it does not work offline."
    >
      Install
    </Button>
  );
}

/**
 * Whether this console is still being told things.
 *
 * A dropped event stream is invisible by default: `EventSource` retries quietly and the lists
 * simply stop moving, which looks exactly like a quiet afternoon. So the state is on screen,
 * always — green while the socket is up, amber the moment it is not, with the time of the last
 * frame so "nothing has happened" can be told apart from "nothing has reached us".
 */
function StreamBadge(): ReactNode {
  const { state, lastEventAt, reconnects } = useStreamStatus();
  const label =
    state === 'live' ? 'Live' : state === 'connecting' ? 'Connecting…' : state === 'reconnecting' ? 'Reconnecting…' : 'Stream off';
  const tone =
    state === 'live'
      ? 'bg-ok-soft text-ok-soft-fg'
      : state === 'off'
        ? 'bg-danger-soft text-danger-soft-fg'
        : 'bg-warn-soft text-warn-soft-fg';
  const detail =
    state === 'live'
      ? `Server-sent events over /api/v1/events. Last frame ${lastEventAt ? formatAgo(new Date(lastEventAt).toISOString()) : 'not yet'}.${reconnects ? ` Reconnected ${reconnects}×.` : ''}`
      : state === 'off'
        ? 'The event stream is closed — this page will not update on its own. Reload to reopen it.'
        : 'The event stream dropped and is being reopened. Anything shown may be out of date until it returns.';
  return (
    <span
      role="status"
      title={detail}
      aria-label={`${label}. ${detail}`}
      className={clsx('flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold', tone)}
      data-testid="stream-badge"
      data-state={state}
    >
      <span aria-hidden className={clsx('size-1.5 rounded-full', state === 'live' ? 'bg-ok' : state === 'off' ? 'bg-danger' : 'bg-warn animate-pulse')} />
      {label}
      {reconnects > 0 && state === 'live' && <span className="font-normal opacity-70">· {reconnects} reconnect{reconnects > 1 ? 's' : ''}</span>}
    </span>
  );
}
